import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { 
  Download, FileText, Loader2,
  Calendar, Users, Hash, Clock, RefreshCw, 
  ArrowLeft, MoreVertical, Pencil, Sparkles, Eye, MessageSquare, Tag
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Tables } from "@/integrations/supabase/types";
import { DocumentComments } from "@/components/documents/DocumentComments";
import { QuickTypeEdit } from "@/components/documents/QuickTypeEdit";
import { DataSensitivityBadge } from "@/components/documents/DataSensitivityBadge";
import { NeedsReviewBadge } from "@/components/documents/NeedsReviewBadge";
import { ReviewValidationDialog } from "@/components/documents/ReviewValidationDialog";
import { getTypeConfig, getFamilyConfig, getFamilyFromCategory } from "@/constants/ui-mapping";
import { SEO } from "@/components/seo/SEO";
import { Skeleton } from "@/components/ui/skeleton";
import { SmartDocumentViewer } from "@/components/viewer/SmartDocumentViewer";
import { isExpirableDocType } from "@/lib/documentExpiration";

type DocumentType = Tables<"documents"> & { taxonomy_family?: string | null };

// Animation variants
const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const cardVariant = {
  initial: { opacity: 0, y: 15, scale: 0.98 },
  animate: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { type: "spring" as const, stiffness: 300, damping: 25 }
  }
};

export default function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [document, setDocument] = useState<DocumentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"details" | "document">("details");
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [testingV2, setTestingV2] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);

  // Parse key_fields safely
  const keyFields = document?.key_fields as Record<string, any> | null;
  const isExpirable = document?.doc_type ? isExpirableDocType(document.doc_type) : false;

  // Fetch document data
  useEffect(() => {
    if (!id) return;

    const fetchDocument = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase
        .from("documents")
        .select("*, document_taxonomy(family)")
        .eq("id", id)
        .eq("user_id", user.id)
        .single();

      if (error || !data) {
        toast.error("Document non trouvé");
        navigate("/documents");
        return;
      }

      // Extract taxonomy_family from joined data
      const { document_taxonomy, ...docData } = data as any;
      const taxonomy_family = document_taxonomy?.family || null;
      setDocument({ ...docData, taxonomy_family });
      setLoading(false);

      // Update last_viewed_at for "Récents" tracking (fire and forget)
      supabase
        .from("documents")
        .update({ last_viewed_at: new Date().toISOString() })
        .eq("id", id)
        .then(({ error }) => {
          if (error) console.warn("[DocumentDetail] Failed to update last_viewed_at:", error);
        });
    };

    fetchDocument();
  }, [id, navigate]);

  // Fetch file URL when document is loaded
  useEffect(() => {
    if (!document) return;

    const fetchFile = async () => {
      setIsFetching(true);
      try {
        const { data, error: signError } = await supabase.storage
          .from("documents")
          .createSignedUrl(document.file_path, 300);

        if (signError) throw signError;
        if (!data?.signedUrl) throw new Error("URL signée indisponible");

        setFileUrl(data.signedUrl);
      } catch (err: any) {
        console.error("[DocumentDetail] Error:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchFile();
  }, [document?.id, document?.file_path]);

  const handleDownload = async () => {
    if (!document) return;
    try {
      const { data, error } = await supabase.storage
        .from("documents")
        .download(document.file_path);
      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = document.file_name;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Téléchargement démarré");
    } catch (err) {
      console.error("Download error:", err);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleReanalyze = async () => {
    if (!document) return;
    setReanalyzing(true);
    try {
      const { data, error: extractError } = await supabase.functions.invoke(
        "extract-document-info",
        {
          body: {
            fileName: document.file_name,
            mimeType: document.mime_type,
            docType: document.doc_type,
            filePath: document.file_path,
          },
        }
      );

      if (extractError) throw extractError;

      const updates: Record<string, any> = { updated_at: new Date().toISOString() };
      if (data.suggested_title) updates.title = data.suggested_title;
      if (data.summary) updates.ai_summary = data.summary;
      if (data.doc_date) updates.doc_date = data.doc_date;
      if (data.expires_at !== undefined) updates.expires_at = data.expires_at;
      if (data.extracted_text) updates.extracted_text = data.extracted_text;

      const keyFieldsUpdate: Record<string, any> = {};
      if (data.amount) keyFieldsUpdate.amount = data.amount;
      if (data.currency) keyFieldsUpdate.currency = data.currency;
      if (data.parties?.length) keyFieldsUpdate.parties = data.parties;
      if (data.reference) keyFieldsUpdate.reference = data.reference;
      if (Object.keys(keyFieldsUpdate).length > 0) updates.key_fields = keyFieldsUpdate;

      const { error: updateError } = await supabase
        .from("documents")
        .update(updates)
        .eq("id", document.id);

      if (updateError) throw updateError;
      
      // Refresh document data
      const { data: refreshed } = await supabase
        .from("documents")
        .select("*")
        .eq("id", document.id)
        .single();
      
      if (refreshed) setDocument(refreshed);
      toast.success("Document réanalysé avec succès !");
    } catch (err: any) {
      console.error("Reanalyze error:", err);
      toast.error("Erreur lors de la réanalyse: " + (err.message || "Erreur inconnue"));
    } finally {
      setReanalyzing(false);
    }
  };

  const handleTestV2 = async () => {
    if (!document) return;
    setTestingV2(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "classify-document-v2",
        {
          body: {
            fileName: document.file_name,
            mimeType: document.mime_type,
            filePath: document.file_path,
          },
        }
      );

      if (error) throw error;

      console.log("[V2 Test Result]", data);
      
      const currentType = document.doc_type;
      const v2Type = data.document_type;
      const confidence = data.confidence_score;
      const status = data.status;
      
      toast.info(
        `V2: ${data.type_label || v2Type} (${confidence}% - ${status})\n` +
        `Actuel: ${currentType}\n` +
        `Sensibilité: ${data.data_sensitivity}`,
        { duration: 8000 }
      );

      // If V2 is more confident and different, offer to update
      if (v2Type !== currentType && confidence >= 85) {
        const confirmed = window.confirm(
          `La V2 suggère "${data.type_label}" avec ${confidence}% de confiance.\n` +
          `Voulez-vous appliquer cette classification ?`
        );
        
        if (confirmed) {
          const updateData: Record<string, unknown> = {
            taxonomy_slug: data.taxonomy_slug,
            doc_type: data.core_type || v2Type,
            category: data.category,
            data_sensitivity: data.data_sensitivity,
            extracted_data: data.extracted_data,
            suggested_actions: data.suggested_actions,
            confidence_score: confidence,
            status: status,
            classification_method: "ai_v2",
          };
          
          if (data.issuer_name_norm) {
            updateData.issuer_name_norm = data.issuer_name_norm;
          }
          
          const uf = data.universal_fields;
          if (uf) {
            if (uf.issuer_name) updateData.issuer_name = uf.issuer_name;
            if (uf.issue_date) updateData.issue_date = uf.issue_date;
            if (uf.due_date) updateData.due_date = uf.due_date;
            if (uf.total_amount != null) updateData.total_amount = uf.total_amount;
            if (uf.reference_number) updateData.reference_number = uf.reference_number;
            if (uf.recipient_name) updateData.recipient_name = uf.recipient_name;
          }
          
          const { error: updateError } = await supabase
            .from("documents")
            .update(updateData)
            .eq("id", document.id);

          if (updateError) throw updateError;

          const { data: refreshed } = await supabase
            .from("documents")
            .select("*")
            .eq("id", document.id)
            .single();
          
          if (refreshed) setDocument(refreshed);
          toast.success("Classification V2 appliquée !");
        }
      }
    } catch (err: any) {
      console.error("V2 Test error:", err);
      toast.error("Erreur V2: " + (err.message || "Erreur inconnue"));
    } finally {
      setTestingV2(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const typeConfig = document?.doc_type ? getTypeConfig(document.doc_type) : null;

  // Skeleton loader for loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex flex-col">
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border/50">
          <div className="flex items-center justify-between px-4 h-16">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="h-5 w-48 rounded-lg" />
            <Skeleton className="h-9 w-9 rounded-lg" />
          </div>
        </header>
        
        <div className="flex-1 flex p-6 gap-6">
          <div className="w-[30%] space-y-4">
            <Skeleton className="h-48 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-32 w-full rounded-2xl" />
          </div>
          
          <div className="flex-1 flex flex-col gap-4">
            <Skeleton className="h-12 w-full rounded-xl" />
            <Skeleton className="flex-1 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!document) {
    return null;
  }

  // Reusable Details Panel content
  const DetailsPanel = () => (
    <motion.div 
      className="space-y-5"
      variants={staggerContainer}
      initial="initial"
      animate="animate"
    >
      {/* Main Info Card */}
      <motion.div variants={cardVariant}>
        <Card className="border-border/50 overflow-hidden bg-gradient-to-br from-card to-card/80 shadow-lg hover:shadow-xl transition-all duration-300 group">
          <CardContent className="p-5 space-y-5">
            {/* Header with badges */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <DataSensitivityBadge 
                    sensitivity={(document as any).data_sensitivity as "low" | "medium" | "high" | null} 
                  />
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <NeedsReviewBadge 
                    status={document.status}
                    confidenceScore={document.confidence_score}
                    onClick={() => setShowReviewDialog(true)}
                  />
                </motion.div>
              </div>

              <div className="flex items-center gap-1">
                <QuickTypeEdit
                  document={document}
                  onUpdate={(updated) => setDocument(updated)}
                />
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-200"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Amount display */}
            {keyFields?.amount && (
              <motion.div 
                className="relative"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent rounded-xl blur-xl" />
                <p className="relative text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
                  {keyFields.amount} <span className="text-2xl text-muted-foreground">{keyFields.currency || "€"}</span>
                </p>
              </motion.div>
            )}

            {/* AI Summary */}
            {document.ai_summary && (
              <div className="relative">
                <div className="absolute -left-1 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-primary/30 rounded-full" />
                <p className="text-sm text-muted-foreground leading-relaxed pl-4 italic">
                  "{document.ai_summary}"
                </p>
              </div>
            )}

            {/* Metadata grid */}
            <div className="grid grid-cols-2 gap-4 pt-3">
              {(document.taxonomy_family || document.category) && (
                <motion.div 
                  className="group/item"
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                    <Tag className="w-3 h-3" />
                    <span>Catégorie</span>
                  </div>
                  {(() => {
                    const family = document.taxonomy_family || getFamilyFromCategory(document.category);
                    const familyConfig = getFamilyConfig(family);
                    return (
                      <span className={cn(
                        "inline-flex items-center text-xs px-3 py-1.5 rounded-full font-medium shadow-sm",
                        familyConfig.color
                      )}>
                        {familyConfig.emoji} {familyConfig.label}
                      </span>
                    );
                  })()}
                </motion.div>
              )}

              {keyFields?.parties?.length > 0 && (
                <motion.div 
                  className="group/item"
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                    <Users className="w-3 h-3" />
                    <span>Fournisseur</span>
                  </div>
                  <p className="font-medium text-sm">{keyFields.parties[0]}</p>
                </motion.div>
              )}

              {document.doc_date && (
                <motion.div 
                  className="group/item"
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                    <Calendar className="w-3 h-3" />
                    <span>Date d'émission</span>
                  </div>
                  <p className="font-medium text-sm">{formatDate(document.doc_date)}</p>
                </motion.div>
              )}

              {isExpirable && (
                <motion.div 
                  className="group/item"
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                    <Clock className="w-3 h-3" />
                    <span>Date d'échéance</span>
                  </div>
                  <p className="font-medium text-sm">
                    {document.expires_at ? formatDate(document.expires_at) : "non définie"}
                  </p>
                </motion.div>
              )}

              {keyFields?.reference && (
                <motion.div 
                  className="col-span-2 group/item"
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                    <Hash className="w-3 h-3" />
                    <span>Numéro de facture</span>
                  </div>
                  <p className="font-medium text-sm font-mono bg-secondary/50 px-2 py-1 rounded-md inline-block">
                    {keyFields.reference}
                  </p>
                </motion.div>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Invoice status quick actions */}
      {document.doc_type === 'invoice' && (
        <motion.div variants={cardVariant} className="space-y-3">
          <p className="text-sm font-medium flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            Cette facture est...
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { icon: "💳", label: "À payer", color: "destructive", active: false },
              { icon: "✓", label: "Payée", color: "primary", active: true },
              { icon: "⏳", label: "En attente", color: "muted", active: false }
            ].map((status) => (
              <motion.div
                key={status.label}
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <Button 
                  variant="outline" 
                  className={cn(
                    "flex flex-col items-center gap-1.5 h-auto py-4 border-2 w-full transition-all duration-300",
                    status.active && "border-primary bg-primary/5 shadow-md"
                  )}
                >
                  <div className={cn(
                    "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300",
                    status.active ? "bg-primary/20 scale-110" : "bg-secondary"
                  )}>
                    <span className={cn(
                      "text-lg",
                      status.active ? "text-primary" : "text-muted-foreground"
                    )}>{status.icon}</span>
                  </div>
                  <span className={cn(
                    "text-xs",
                    status.active && "font-semibold text-primary"
                  )}>{status.label}</span>
                </Button>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Comments section */}
      <motion.div variants={cardVariant} className="pt-2">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Commentaires</span>
        </div>
        <DocumentComments documentId={document.id} />
      </motion.div>
    </motion.div>
  );

  // Document Viewer component using the unified viewer
  const ViewerPanel = () => (
    <div className="flex-1 min-h-0 flex flex-col">
      {isFetching ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : fileUrl && document.mime_type ? (
        <SmartDocumentViewer
          url={fileUrl}
          title={document.title}
          mimeType={document.mime_type}
          className="flex-1 min-h-0"
        />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <FileText className="w-16 h-16 text-muted-foreground/50" />
          <p className="text-muted-foreground text-sm">Aperçu non disponible</p>
          <Button variant="outline" size="sm" onClick={handleDownload} className="gap-2">
            <Download className="w-4 h-4" />
            Télécharger
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <SEO 
        title={document.title}
        description={document.ai_summary || "Détail du document"}
      />
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex flex-col">
        {/* Professional Header */}
        <motion.header 
          className="sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border/50"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center justify-between px-4 h-16">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-10 w-10 rounded-xl hover:bg-primary/10 hover:text-primary transition-all duration-200"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </motion.div>
            
            <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-4">
              <motion.p 
                className="text-sm font-semibold text-foreground truncate max-w-[70vw] md:max-w-[40vw]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                {document.title}
              </motion.p>
              {typeConfig && (
                <motion.div
                  className="flex items-center gap-1.5 mt-0.5"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <span className="text-xs">{typeConfig.emoji}</span>
                  <span className="text-xs text-muted-foreground">{typeConfig.label}</span>
                </motion.div>
              )}
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-10 w-10 rounded-xl hover:bg-primary/10 hover:text-primary transition-all duration-200"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </motion.div>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="bg-popover/95 backdrop-blur-md border-border/50 shadow-xl rounded-xl min-w-[180px]"
              >
                <DropdownMenuItem 
                  onClick={handleDownload}
                  className="gap-3 py-2.5 cursor-pointer hover:bg-primary/10 rounded-lg mx-1"
                >
                  <Download className="w-4 h-4 text-primary" />
                  Télécharger
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleReanalyze} 
                  disabled={reanalyzing}
                  className="gap-3 py-2.5 cursor-pointer hover:bg-primary/10 rounded-lg mx-1"
                >
                  <RefreshCw className={cn("w-4 h-4 text-primary", reanalyzing && "animate-spin")} />
                  Réanalyser (V1)
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleTestV2} 
                  disabled={testingV2}
                  className="gap-3 py-2.5 cursor-pointer hover:bg-primary/10 rounded-lg mx-1"
                >
                  <Sparkles className={cn("w-4 h-4 text-primary", testingV2 && "animate-pulse")} />
                  {testingV2 ? "Test V2 en cours..." : "Tester Classification V2"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.header>

        {/* Viewer layout */}
        {isMobile ? (
          <div className="flex-1 min-h-0 flex flex-col">
            <Tabs 
              value={activeTab} 
              onValueChange={(v) => setActiveTab(v as "details" | "document")} 
              className="flex-1 min-h-0 flex flex-col"
            >
              <TabsList className="grid w-full grid-cols-2 shrink-0 rounded-none border-b border-border/50 bg-background/80 backdrop-blur-sm h-12">
                <TabsTrigger 
                  value="details" 
                  className="text-sm font-medium data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none transition-all duration-200"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Détails
                </TabsTrigger>
                <TabsTrigger 
                  value="document" 
                  className="text-sm font-medium data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none transition-all duration-200"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Justificatif
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="flex-1 min-h-0 overflow-auto p-4 m-0 bg-gradient-to-b from-background to-secondary/10">
                <DetailsPanel />
              </TabsContent>

              <TabsContent value="document" className="flex-1 min-h-0 flex flex-col m-0">
                <ViewerPanel />
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
            {/* Left panel - Details (resizable) */}
            <ResizablePanel defaultSize={30} minSize={20} maxSize={50} className="bg-gradient-to-b from-background to-secondary/10">
              <div className="h-full overflow-auto p-6">
                <DetailsPanel />
              </div>
            </ResizablePanel>
            
            <ResizableHandle 
              withHandle 
              className="bg-border/30 hover:bg-primary/30 transition-colors duration-200 data-[resize-handle-active]:bg-primary/50" 
            />
            
            {/* Right panel - Document viewer */}
            <ResizablePanel defaultSize={70} minSize={50} className="flex flex-col overflow-hidden">
              <ViewerPanel />
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>

      {/* Review Validation Dialog */}
      <ReviewValidationDialog
        open={showReviewDialog}
        onOpenChange={setShowReviewDialog}
        documentId={document.id}
        currentType={document.doc_type}
        currentCategory={document.category}
        confidenceScore={document.confidence_score}
        onValidated={() => {
          supabase
            .from("documents")
            .select("*")
            .eq("id", document.id)
            .single()
            .then(({ data }) => {
              if (data) setDocument(data);
            });
        }}
      />
    </>
  );
}
